import { Injectable } from '@angular/core';
import * as ExcelJS from 'exceljs';
import FileSaver from 'file-saver';
import { TemplateColumnConfig } from '../models/template-column-config';

const EXCEL_TYPE =
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
const EXCEL_EXTENSION = '.xlsx';

@Injectable({ providedIn: 'root' })
export class ExcelService {
  constructor() {}

  public async exportAsExcelFile(
    workbookData: {
      workSheet: string;
      columns: TemplateColumnConfig[];
      rows: any[];
      dropdowns?: Record<string, string[]>;
    }[],
    excelFileName: string,
    allQuestions?: { title: string; fullText: string; category: string }[],
  ) {
    const workbook = new ExcelJS.Workbook();
    const getNumRows = (workSheet: string) => {
      if (workSheet === 'Assignments' || workSheet === 'Exams') {
        return 1;
      }
      return 100000;
    };

    // Excel/Sheets: use hidden Lookups sheet and range reference
    // --- Unified Lookups Sheet Logic for Assignment and Section Questions ---
    // Collect all unique question titles from both sheets
    const assignmentQuestionsSheet = workbookData.find(
      (sheet) => sheet.workSheet === 'Assignment Questions',
    );
    const sectionQuestionsSheet = workbookData.find(
      (sheet) => sheet.workSheet === 'Section Questions',
    );
    let allDropdownTitles: string[] = [];
    if (assignmentQuestionsSheet) {
      allDropdownTitles = allDropdownTitles.concat(
        assignmentQuestionsSheet.dropdowns?.['Question Title'] || [],
      );
    }
    if (sectionQuestionsSheet) {
      allDropdownTitles = allDropdownTitles.concat(
        sectionQuestionsSheet.dropdowns?.['Question Title'] || [],
      );
    }
    // Remove duplicates
    allDropdownTitles = Array.from(new Set(allDropdownTitles));
    // Build unified lookup rows from allQuestions
    const unifiedLookupRows = allDropdownTitles.map((title) => {
      const found = (allQuestions || []).find((q) => q.title === title);
      return {
        title,
        fullText: found ? found.fullText : '',
        category: found ? found.category : '',
      };
    });
    // Populate Lookups sheet with unified data
    let lookupsSheet = workbook.getWorksheet('Lookups');
    if (!lookupsSheet) {
      lookupsSheet = workbook.addWorksheet('Lookups');
      lookupsSheet.state = 'veryHidden';
    }
    lookupsSheet.getCell(1, 1).value = 'Question Title';
    lookupsSheet.getCell(1, 2).value = 'Full Text';
    lookupsSheet.getCell(1, 3).value = 'Category';
    unifiedLookupRows.forEach((row, i) => {
      lookupsSheet!.getCell(i + 2, 1).value = row.title;
      lookupsSheet!.getCell(i + 2, 2).value = row.fullText;
      lookupsSheet!.getCell(i + 2, 3).value = row.category;
    });
    // --- End Unified Lookups Sheet Logic ---

    // Dropdown logic: collect all dropdowns from all sheets
    const lookupColumns: Record<string, number> = {};
    let lookupColIdx = lookupsSheet.columnCount + 1;
    const allDropdowns: Record<string, string[]> = {};
    workbookData.forEach(({ columns, dropdowns }) => {
      columns.forEach((col) => {
        let options: string[] | undefined;
        if (col.type === 'enum' && col.enumValues) {
          options = col.enumValues;
        } else if (dropdowns && dropdowns[col.label]) {
          options = dropdowns[col.label];
        }
        if (options && options.length > 0) {
          if (!allDropdowns[col.label]) {
            allDropdowns[col.label] = Array.from(
              new Set(options.map((opt) => opt.trim().replace(/,/g, '').replace(/"/g, ''))),
            );
          }
        }
      });
    });
    // Write each dropdown's options to a column in Lookups (after VLOOKUP columns)
    Object.entries(allDropdowns).forEach(([label, options]) => {
      // If this dropdown is already in the first 3 columns (VLOOKUP), skip
      if (['Question Title', 'Full Text', 'Category'].includes(label)) return;
      lookupColumns[label] = lookupColIdx;
      lookupsSheet!.getCell(1, lookupColIdx).value = label;
      options.forEach((opt, i) => {
        lookupsSheet!.getCell(i + 2, lookupColIdx).value = opt;
      });
      lookupColIdx++;
    });
    workbookData.forEach(({ workSheet, columns, rows, dropdowns }) => {
      const sheet = workbook.addWorksheet(workSheet);
      sheet.columns = columns.map((col) => ({
        header: col.label,
        key: col.label,
      }));
      const numRows = getNumRows(workSheet);
      (rows || []).forEach((row, rowIdx) => {
        const rowData: any = {};
        columns.forEach((col) => {
          rowData[col.label] = '';
        });
        sheet.addRow(rowData);
      });
      columns.forEach((col, colIdx) => {
        let options: string[] | undefined;
        if (col.type === 'enum' && col.enumValues) {
          options = col.enumValues;
        } else if (dropdowns && dropdowns[col.label]) {
          options = dropdowns[col.label];
        }
        // Assignment Questions advanced logic
        if (workSheet === 'Assignment Questions' && unifiedLookupRows.length > 0) {
          const questionTitleColIdx = columns.findIndex((c) => c.label === 'Question Title');
          const questionTitleExcelCol = this.getExcelColumnLetter(questionTitleColIdx + 1);
          if (col.label === 'Full Text') {
            for (let i = 2; i <= numRows + 1; i++) {
              sheet.getCell(i, colIdx + 1).value = {
                formula: `IFERROR(VLOOKUP($${questionTitleExcelCol}${i},Lookups!$A$2:$C$${
                  unifiedLookupRows.length + 1
                },2,FALSE),"")`,
              };
            }
            return;
          }
          if (col.label === 'Category') {
            for (let i = 2; i <= numRows + 1; i++) {
              sheet.getCell(i, colIdx + 1).value = {
                formula: `IFERROR(VLOOKUP($${questionTitleExcelCol}${i},Lookups!$A$2:$C$${
                  unifiedLookupRows.length + 1
                },3,FALSE),"")`,
              };
            }
            return;
          }
          if (col.label === 'Question Title') {
            const dropdownRange = `Lookups!$A$2:$A$${unifiedLookupRows.length + 1}`;
            for (let i = 2; i <= numRows + 1; i++) {
              sheet.getCell(i, colIdx + 1).dataValidation = {
                type: 'list',
                allowBlank: true,
                formulae: [dropdownRange],
                showErrorMessage: true,
                errorTitle: `Invalid ${col.label}`,
                error: `Please select a value from the list for ${col.label}.`,
              };
            }
            return;
          }
        }
        // Exams auto-calculation logic
        if (workSheet === 'Exams') {
          if (col.label === 'Total Marks (Auto-calculated)') {
            for (let i = 2; i <= numRows + 1; i++) {
              sheet.getCell(i, colIdx + 1).value = {
                formula: `IFERROR(SUM('Exam Sections'!F2:F100000),0)`,
              };
            }
            return;
          }
          if (col.label === 'Total Questions (Auto-calculated)') {
            for (let i = 2; i <= numRows + 1; i++) {
              sheet.getCell(i, colIdx + 1).value = {
                formula: `IFERROR(SUM('Exam Sections'!C2:C100000),0)`,
              };
            }
            return;
          }
        }
        // Exam Sections auto-calculation logic
        if (workSheet === 'Exam Sections') {
          const numberOfQuestionsColIdx = columns.findIndex(
            (c) => c.label === 'Number of Questions',
          );
          const marksForCorrectColIdx = columns.findIndex(
            (c) => c.label === 'Marks for Correct Answer',
          );
          const numberOfQuestionsCol = this.getExcelColumnLetter(numberOfQuestionsColIdx + 1);
          const marksForCorrectCol = this.getExcelColumnLetter(marksForCorrectColIdx + 1);

          if (col.label === 'Section Total Marks (Auto-calculated)') {
            for (let i = 2; i <= numRows + 1; i++) {
              sheet.getCell(i, colIdx + 1).value = {
                formula: `IFERROR(${numberOfQuestionsCol}${i}*${marksForCorrectCol}${i},0)`,
              };
            }
            return;
          }
        }
        // Section Questions advanced logic
        if (workSheet === 'Section Questions' && unifiedLookupRows.length > 0) {
          const sectionQuestionTitleColIdx = columns.findIndex((c) => c.label === 'Question Title');
          const questionTitleExcelCol = this.getExcelColumnLetter(sectionQuestionTitleColIdx + 1);
          if (col.label === 'Full Text') {
            for (let i = 2; i <= numRows + 1; i++) {
              sheet.getCell(i, colIdx + 1).value = {
                formula: `IFERROR(VLOOKUP($${questionTitleExcelCol}${i},Lookups!$A$2:$C$${
                  unifiedLookupRows.length + 1
                },2,FALSE),"")`,
              };
            }
            return;
          }
          if (col.label === 'Category') {
            for (let i = 2; i <= numRows + 1; i++) {
              sheet.getCell(i, colIdx + 1).value = {
                formula: `IFERROR(VLOOKUP($${questionTitleExcelCol}${i},Lookups!$A$2:$C$${
                  unifiedLookupRows.length + 1
                },3,FALSE),"")`,
              };
            }
            return;
          }
          if (col.label === 'Question Title') {
            const dropdownRange = `Lookups!$A$2:$A$${unifiedLookupRows.length + 1}`;
            for (let i = 2; i <= numRows + 1; i++) {
              sheet.getCell(i, colIdx + 1).dataValidation = {
                type: 'list',
                allowBlank: true,
                formulae: [dropdownRange],
                showErrorMessage: true,
                errorTitle: `Invalid ${col.label}`,
                error: `Please select a value from the list for ${col.label}.`,
              };
            }
            return;
          }
        }
        // Normal dropdown logic for all other columns
        if (options && options.length > 0) {
          options = Array.from(
            new Set(options.map((opt) => opt.trim().replace(/,/g, '').replace(/"/g, ''))),
          );
          const useLookupSheet =
            options.length > 0 && (options.join(',').length > 200 || options.length > 10);
          if (useLookupSheet && lookupColumns[col.label]) {
            const colLetter = this.getExcelColumnLetter(lookupColumns[col.label]);
            const range = `Lookups!$${colLetter}$2:$${colLetter}$${options.length}`;
            for (let i = 2; i <= numRows + 1; i++) {
              sheet.getCell(i, colIdx + 1).dataValidation = {
                type: 'list',
                allowBlank: true,
                formulae: [range],
                showErrorMessage: true,
                errorTitle: `Invalid ${col.label}`,
                error: `Please select a value from the list for ${col.label}.`,
              };
            }
          } else if (options.length > 0) {
            let formula = `"${options.join(',')}"`;
            if (formula.length > 255) {
              let maxOptions = options.length;
              while (formula.length > 255 && maxOptions > 0) {
                maxOptions--;
                formula = `"${options.slice(0, maxOptions).join(',')}"`;
              }
            }
            for (let i = 2; i <= numRows + 1; i++) {
              sheet.getCell(i, colIdx + 1).dataValidation = {
                type: 'list',
                allowBlank: true,
                formulae: [formula],
                showErrorMessage: true,
                errorTitle: `Invalid ${col.label}`,
                error: `Please select a value from the list for ${col.label}.`,
              };
            }
          }
        }
      });
    });
    const buffer = await workbook.xlsx.writeBuffer();
    this.saveAsExcelFile(buffer, excelFileName);
  }

  private saveAsExcelFile(buffer: any, fileName: string): void {
    const data: Blob = new Blob([buffer], {
      type: EXCEL_TYPE,
    });
    FileSaver.saveAs(data, fileName + EXCEL_EXTENSION);
  }

  private getExcelColumnLetter(col: number): string {
    let letter = '';
    while (col > 0) {
      const temp = (col - 1) % 26;
      letter = String.fromCharCode(temp + 65) + letter;
      col = Math.floor((col - temp - 1) / 26);
    }
    return letter;
  }
}
